type VCbCrPos = {
  v: number;
  cb: number;
  cr: number;
  x: number;
  y: number;
  xf: number;
  yf: number;
  pad0: number;
};

type HalfMoonColors = {
  vcb0: VCbCrPos;
  vcb1: VCbCrPos;
};

type SpinDebug = {
  normalized_up: [number, number];
  split_dir: [number, number];
  ang_degrees: number;
  ang_wrapped: number;
  spin: number;
  circle: number;
};

type CircleColorEvidence = {
  blueScore: number;
  greenScore: number;
  redScore: number;
  blackScore: number;
  unknownScore: number;
  totalWeight: number;
  colorWeight: number;
  blueGreenScore: number;
  redVsBlueGreen: number;
  blueFracColor: number;
  greenFracColor: number;
  redFracColor: number;
  blueHits: number;
  greenHits: number;
  redHits: number;
  blackHits: number;
  unknownHits: number;
  sampleCount: number;
  hasBlue: number;
  hasGreen: number;
  hasRed: number;
  hasBlack: number;
};

type Blob2 = {
  index: number;
  byteOffset: number;
  strideBytes: number;
  strideWords: number;

  alive: number;
  x: number;
  y: number;
  width: number;
  height: number;

  nn_0: number;
  nn_dist_1: number;
  nn_1: number;
  nn_dist_0: number;
  nn_2: number;
  nn_dist_2: number;
  sticker_count: number;
  pad0: number;

  color_pair: HalfMoonColors;
  ndir: [number, number];

  orientation_confidence: number;
  circle_type: number;
  color_index: number;
  blue_perc: number;
  green_perc: number;
  red_perc: number;
  black_perc: number;
  fail_code: number;
  pad1: number;

  spin_debug: SpinDebug;
  evidence: CircleColorEvidence;
};

const BLOB2_STRIDE_BYTES = 288;
const BLOB2_STRIDE_WORDS = BLOB2_STRIDE_BYTES / 4; // 72
const COLOR_INDEX_BLUE =  0;
const COLOR_INDEX_GREEN = 1;
const COLOR_INDEX_RED = 2;

function f32(dv: DataView, base: number, off: number): number {
  return dv.getFloat32(base + off, true);
}

function i32(dv: DataView, base: number, off: number): number {
  return dv.getInt32(base + off, true);
}

function u32(dv: DataView, base: number, off: number): number {
  return dv.getUint32(base + off, true);
}

function readVCbCrPos(dv: DataView, base: number): VCbCrPos {
  return {
    v:   f32(dv, base, 0),
    cb:  f32(dv, base, 4),
    cr:  f32(dv, base, 8),
    x:   i32(dv, base, 12),
    y:   i32(dv, base, 16),
    xf:  f32(dv, base, 20),
    yf:  f32(dv, base, 24),
    pad0:i32(dv, base, 28),
  };
}

function readHalfMoonColors(dv: DataView, base: number): HalfMoonColors {
  return {
    vcb0: readVCbCrPos(dv, base + 0),
    vcb1: readVCbCrPos(dv, base + 32),
  };
}

function readSpinDebug(dv: DataView, base: number): SpinDebug {
  return {
    normalized_up: [f32(dv, base, 0), f32(dv, base, 4)],
    split_dir:     [f32(dv, base, 8), f32(dv, base, 12)],
    ang_degrees:   f32(dv, base, 16),
    ang_wrapped:   f32(dv, base, 20),
    spin:          i32(dv, base, 24),
    circle:        i32(dv, base, 28),
  };
}

function readCircleColorEvidence(dv: DataView, base: number): CircleColorEvidence {
  return {
    blueScore:       f32(dv, base, 0),
    greenScore:      f32(dv, base, 4),
    redScore:        f32(dv, base, 8),
    blackScore:      f32(dv, base, 12),
    unknownScore:    f32(dv, base, 16),
    totalWeight:     f32(dv, base, 20),
    colorWeight:     f32(dv, base, 24),
    blueGreenScore:  f32(dv, base, 28),
    redVsBlueGreen:  f32(dv, base, 32),
    blueFracColor:   f32(dv, base, 36),
    greenFracColor:  f32(dv, base, 40),
    redFracColor:    f32(dv, base, 44),
    blueHits:        i32(dv, base, 48),
    greenHits:       i32(dv, base, 52),
    redHits:         i32(dv, base, 56),
    blackHits:       i32(dv, base, 60),
    unknownHits:     i32(dv, base, 64),
    sampleCount:     i32(dv, base, 68),
    hasBlue:         u32(dv, base, 72),
    hasGreen:        u32(dv, base, 76),
    hasRed:          u32(dv, base, 80),
    hasBlack:        u32(dv, base, 84),
  };
}

export function readBlob2At(words: Uint32Array, index: number): Blob2 {
  const dv = new DataView(words.buffer, words.byteOffset, words.byteLength);
  const base = index * BLOB2_STRIDE_BYTES;

  return {
    index,
    byteOffset: base,
    strideBytes: BLOB2_STRIDE_BYTES,
    strideWords: BLOB2_STRIDE_WORDS,

    alive: i32(dv, base, 0),
    x: i32(dv, base, 4),
    y: i32(dv, base, 8),
    width: i32(dv, base, 12),
    height: i32(dv, base, 16),

    nn_0: i32(dv, base, 20),
    nn_dist_1: f32(dv, base, 24),
    nn_1: i32(dv, base, 28),
    nn_dist_0: f32(dv, base, 32),
    nn_2: i32(dv, base, 36),
    nn_dist_2: f32(dv, base, 40),
    sticker_count: i32(dv, base, 44),
    pad0: i32(dv, base, 48),

    color_pair: readHalfMoonColors(dv, base + 52),
    ndir: [f32(dv, base, 120), f32(dv, base, 124)],

    orientation_confidence: f32(dv, base, 128),
    circle_type: i32(dv, base, 132),
    color_index: i32(dv, base, 136),
    blue_perc: f32(dv, base, 140),
    green_perc: f32(dv, base, 144),
    red_perc: f32(dv, base, 148),
    black_perc: f32(dv, base, 152),
    fail_code: i32(dv, base, 156),
    pad1: i32(dv, base, 160),

    spin_debug: readSpinDebug(dv, base + 164),
    evidence: readCircleColorEvidence(dv, base + 192),
  };
}

export function printBlob2(words: Uint32Array, index: number): void {
  const b = readBlob2At(words, index);
  console.log(`Blob2[${index}]`, JSON.stringify(b, null, 2));
}

export function printBlob2Range(words: Uint32Array, start: number, count: number): void {
  for (let i = 0; i < count; i++) {
    printBlob2(words, start + i);
  }
}

export function findAliveBlobIndices(words: Uint32Array): number[] {
  const blobCount = Math.floor(words.length / BLOB2_STRIDE_WORDS);
  const out: number[] = [];

  for (let i = 0; i < blobCount; i++) {
    const alive = words[i * BLOB2_STRIDE_WORDS + 0];
    if (alive !== 0) out.push(i);
  }

  //console.log("aliveBlobIndices =", out);
  return out;
}

function color_index_color(ci:number):string
{
  switch (ci)
  {
      case COLOR_INDEX_BLUE :  return "blue ("+ci+")";
      case COLOR_INDEX_GREEN:  return "green ("+ci+")";
      case COLOR_INDEX_RED:  return "red ("+ci+")";
  }

   return "unknown color ("+ci+")";
}

export function printAliveBlobsCircleTypes(words: Uint32Array, maxToPrint = 32): void {
  const alive = findAliveBlobIndices(words);
  var list = [];
  for (const idx of alive.slice(0, maxToPrint))
  {
     const b = readBlob2At(words, idx);
    
     if(b.circle_type == 1)
     {
        var s ={index:b.index, circle_type: b.circle_type, color: color_index_color(b.color_index) ,
          cb_corr: b.blue_perc,
            cr_corr: b.green_perc,
            sb: b.red_perc,
            sr: b.black_perc,
            cls: b.fail_code,
         };
        list.push(s);
      }
    
  }
    console.log(`Blob2[${list.length}]`, JSON.stringify(list, null, 2));
   
}
export function printAliveBlobs(words: Uint32Array, maxToPrint = 32): void {
  const alive = findAliveBlobIndices(words);
  for (const idx of alive.slice(0, maxToPrint)) {
    printBlob2(words, idx);
  }
}

